import { uniq } from "lodash"

import { type CLASS_GROUPS } from "#constants/classOptions"
import {
  ALL_SKILL_LEVEL_CUSTOMER_LABELS,
  CLASS_GROUP_TO_SKILL_TYPE,
  getStudentSkill,
} from "#constants/skillLevelConstants"
import {
  EVALUATION_STATUS,
  type EvaluationConstructor,
  NO_PROMO_REASON_LABELS,
  type NoPromoReason,
} from "#customTypes/composedTypes/evaluationTypes"
import { type StudentConstructor } from "#customTypes/composedTypes/studentTypes"
import { type Uuid } from "#customTypes/globalTypes"
import { generateEvalId } from "#utils/evaluationHelpers"
import { isPromoted, ladderFor } from "#utils/promotionRules"

export type ClassroomStudent = {
  studentId: Uuid
  name: string
  fromLabel: string
  toLabel: string
  rung: number
  promoted: boolean
  reasons: string[]
  note: string
}

export type ClassroomClass = {
  classId: Uuid
  name: string
  startTime: string
  ready: boolean
  lowestRung: number
  promoted: ClassroomStudent[]
  staying: ClassroomStudent[]
}

export type ClassroomClassInput = {
  id: Uuid
  name: string
  startTime: string
  semester: string
  group: CLASS_GROUPS
  studentIds: Uuid[]
}

const SETTLED = new Set<EVALUATION_STATUS>([
  EVALUATION_STATUS.APPROVED,
  EVALUATION_STATUS.PRINTED,
  EVALUATION_STATUS.NO_EVAL,
])

const customerLabels: Record<string, string> = ALL_SKILL_LEVEL_CUSTOMER_LABELS

export const customerSkillLabel = (skill: string | undefined): string => {
  if (!skill) return "No level recorded"
  return customerLabels[skill] ?? skill
}

const rungOf = (
  skill: string | undefined,
  classGroup: CLASS_GROUPS
): number => {
  if (!skill) return Number.POSITIVE_INFINITY
  const index = ladderFor(classGroup).indexOf(skill)
  return index === -1 ? Number.POSITIVE_INFINITY : index
}

const byRungThenName = (a: ClassroomStudent, b: ClassroomStudent) =>
  a.rung - b.rung || a.name.localeCompare(b.name)

const reasonLabels = (
  status: EVALUATION_STATUS | undefined,
  reasons: NoPromoReason[] | undefined
): string[] => {
  const labels = (reasons ?? [])
    .map((reason) => NO_PROMO_REASON_LABELS[reason])
    .filter(Boolean)
  if (status === EVALUATION_STATUS.NO_EVAL) return ["No eval", ...labels]
  return labels
}

export const buildClassroomRoster = (
  classes: ClassroomClassInput[],
  studentsById: Record<
    string,
    Pick<StudentConstructor, "name" | "skillHistory">
  >,
  evaluationsById: Record<
    string,
    Pick<
      EvaluationConstructor,
      "status" | "currentSkill" | "nextSkill" | "noPromoReason" | "noPromoText"
    >
  >
): ClassroomClass[] =>
  classes
    .filter((classObj) => !classObj.name.includes("TA"))
    .map((classObj) => {
      const studentIds = uniq(classObj.studentIds)
      const rows = studentIds.map((studentId) => {
        const evaluation =
          evaluationsById[
            generateEvalId(studentId, classObj.semester, classObj.group)
          ]
        const fromSkill =
          evaluation?.currentSkill ||
          getStudentSkill(
            studentsById[studentId]?.skillHistory,
            CLASS_GROUP_TO_SKILL_TYPE[classObj.group],
            classObj.semester
          )
        const toSkill = evaluation?.nextSkill || ""
        const promoted =
          (evaluation?.status === EVALUATION_STATUS.APPROVED ||
            evaluation?.status === EVALUATION_STATUS.PRINTED) &&
          isPromoted(fromSkill, toSkill, classObj.group)
        const fromRung = rungOf(fromSkill, classObj.group)
        return {
          settled: !!evaluation && SETTLED.has(evaluation.status),
          fromRung,
          student: {
            studentId,
            name: studentsById[studentId]?.name ?? "Unknown",
            fromLabel: customerSkillLabel(fromSkill),
            toLabel: customerSkillLabel(toSkill),
            rung: rungOf(promoted ? toSkill : fromSkill, classObj.group),
            promoted,
            reasons: reasonLabels(
              evaluation?.status,
              evaluation?.noPromoReason
            ),
            note: evaluation?.noPromoText?.trim() ?? "",
          } satisfies ClassroomStudent,
        }
      })

      const ready = rows.length > 0 && rows.every((row) => row.settled)
      const students = rows.map((row) => row.student)
      const lowestRung = rows.reduce(
        (lowest, row) => Math.min(lowest, row.fromRung),
        Number.POSITIVE_INFINITY
      )

      return {
        classId: classObj.id,
        name: classObj.name,
        startTime: classObj.startTime,
        ready,
        lowestRung: Number.isFinite(lowestRung) ? lowestRung : 999,
        promoted: students
          .filter((student) => student.promoted)
          .sort(byRungThenName),
        staying: students
          .filter((student) => !student.promoted)
          .sort(byRungThenName),
      }
    })
    .filter(
      (classObj) => classObj.promoted.length + classObj.staying.length > 0
    )
    .sort((a, b) => a.lowestRung - b.lowestRung || a.name.localeCompare(b.name))

export const stayingDetail = (student: ClassroomStudent): string =>
  [...student.reasons, student.note].filter(Boolean).join(" — ")

export type ClassroomCardLine =
  | { kind: "title"; text: string }
  | { kind: "class"; text: string }
  | { kind: "move"; name: string; change: string }
  | { kind: "stay-heading" }
  | { kind: "stay"; name: string; label: string }
  | { kind: "detail"; name: string; text: string }
  | { kind: "footer"; text: string }

export const classroomCardLines = (
  classes: ClassroomClass[],
  detail: boolean,
  semesterLabel: string,
  nextSemesterLabel: string
): ClassroomCardLine[] => {
  const lines: ClassroomCardLine[] = [
    { kind: "title", text: `Who leveled up · ${semesterLabel}` },
  ]
  const details: ClassroomCardLine[] = []

  for (const classObj of classes) {
    lines.push({
      kind: "class",
      text: `${classObj.name} · ${classObj.startTime}`,
    })
    for (const student of classObj.promoted) {
      lines.push({
        kind: "move",
        name: student.name,
        change: `${student.fromLabel} → ${student.toLabel}`,
      })
    }
    if (classObj.staying.length) {
      lines.push({ kind: "stay-heading" })
      for (const student of classObj.staying) {
        lines.push({
          kind: "stay",
          name: student.name,
          label: `stays at ${student.fromLabel}`,
        })
        const text = stayingDetail(student)
        if (detail && text) {
          details.push({
            kind: "detail",
            name: `${student.name} · ${classObj.name}`,
            text,
          })
        }
      }
    }
  }

  lines.push(...details)
  lines.push({
    kind: "footer",
    text: detail
      ? `Detail view — includes teacher notes. ${nextSemesterLabel}`
      : `Summary view — no notes. ${nextSemesterLabel}`,
  })
  return lines
}

const LINE_HEIGHT: Record<ClassroomCardLine["kind"], number> = {
  title: 72,
  class: 56,
  move: 84,
  "stay-heading": 44,
  stay: 56,
  detail: 72,
  footer: 64,
}

const CARD_WIDTH = 1080
const BANNER_HEIGHT = 188
const BODY_TOP = BANNER_HEIGHT + 40
const BOTTOM_PAD = 48

export const classroomCardCanvas = (
  lines: ClassroomCardLine[],
  semesterLabel: string
) => {
  const bodyLines = lines.filter((line) => line.kind !== "title")
  const bodyHeight = bodyLines.reduce(
    (sum, line) => sum + LINE_HEIGHT[line.kind],
    0
  )
  const contentBottom = BODY_TOP + bodyHeight
  return {
    width: CARD_WIDTH,
    height: contentBottom + BOTTOM_PAD,
    bannerHeight: BANNER_HEIGHT,
    bodyTop: BODY_TOP,
    contentBottom,
    bannerLines: ["HONGYUN ART", "Who leveled up", semesterLabel],
    bodyLines,
  }
}

export const downloadClassroomCard = async (
  classes: ClassroomClass[],
  detail: boolean,
  semesterLabel: string,
  nextSemesterLabel: string
): Promise<Blob> => {
  const lines = classroomCardLines(
    classes,
    detail,
    semesterLabel,
    nextSemesterLabel
  )
  const layout = classroomCardCanvas(lines, semesterLabel)
  const canvas = document.createElement("canvas")
  canvas.width = layout.width
  canvas.height = layout.height
  const context = canvas.getContext("2d")
  if (!context) throw new Error("Could not draw the classroom card")

  context.fillStyle = "#fffdf8"
  context.fillRect(0, 0, layout.width, layout.height)
  context.fillStyle = "#EDAF68"
  context.fillRect(0, 0, layout.width, layout.bannerHeight)
  context.fillStyle = "#141400"
  context.font = "600 28px sans-serif"
  context.fillText("HONGYUN ART", 48, 52)
  context.font = "700 48px sans-serif"
  context.fillText("Who leveled up", 48, 108)
  context.font = "28px sans-serif"
  context.fillText(layout.bannerLines[2] ?? semesterLabel, 48, 156)

  let y = layout.bodyTop
  for (const line of layout.bodyLines) {
    context.fillStyle = line.kind === "stay" ? "#8a8a8a" : "#141400"
    if (line.kind === "class") {
      context.font = "700 32px sans-serif"
      context.fillText(line.text, 48, y)
    } else if (line.kind === "move") {
      context.font = "600 30px sans-serif"
      context.fillText(line.name, 48, y)
      context.font = "28px sans-serif"
      context.fillText(line.change, 48, y + 36)
    } else if (line.kind === "stay-heading") {
      context.font = "700 24px sans-serif"
      context.fillStyle = "#8a8a8a"
      context.fillText("No level up", 48, y)
    } else if (line.kind === "stay") {
      context.font = "600 28px sans-serif"
      context.fillText(`${line.name} · ${line.label}`, 48, y)
    } else if (line.kind === "detail") {
      context.font = "600 26px sans-serif"
      context.fillText(line.name, 48, y)
      context.font = "24px sans-serif"
      context.fillText(line.text, 48, y + 32)
    } else {
      context.font = "22px sans-serif"
      context.fillStyle = "#666"
      context.fillText(line.text, 48, y)
    }
    y += LINE_HEIGHT[line.kind]
  }

  const blob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob(resolve, "image/png")
  })
  if (!blob) throw new Error("Could not save the classroom card")
  return blob
}
